// ── Enums ────────────────────────────────────────────────────────────────────

export type UserStatus = 'Active' | 'Inactive' | 'Locked' | 'PendingActivation';

// ── DTOs ─────────────────────────────────────────────────────────────────────

/** Detailed user record returned by GET /users/{id} */
export interface UserDto {
  id:                    string;
  companyId:             string;
  username:              string;
  email:                 string;
  userCode?:             string | null;
  displayName?:          string | null;
  employeeRef?:          string | null;
  userType?:             string | null;
  remarks?:              string | null;
  profileImageUrl?:      string | null;
  firstName:             string;
  lastName:              string;
  fullName:              string;
  phoneNumber?:          string | null;
  status:                UserStatus;
  isSystemUser:          boolean;
  mfaEnabled:            boolean;
  forcePasswordChange:   boolean;
  passwordChangedDate?:  string | null;
  lastLoginDate?:        string | null;
  failedLoginAttempts:   number;
  lockedUntil?:          string | null;
  defaultLocationId?:    string | null;
  defaultLocationName?:  string | null;
  defaultCostCenterId?:  string | null;
  defaultCostCenterName?: string | null;
  createdDate:           string;
  createdByName?:        string | null;
  modifiedDate?:         string | null;
  roles:                 UserRoleSummaryDto[];
  locationAccesses:      UserLocationAccessDto[];
  costCenterAccesses:    UserCostCenterAccessDto[];
  companyAccesses:       UserCompanyAccessDto[];
}

/** Lightweight row for paginated list */
export interface UserListDto {
  id:                  string;
  username:            string;
  email:               string;
  userCode?:           string | null;
  displayName?:        string | null;
  userType?:           string | null;
  profileImageUrl?:    string | null;
  fullName:            string;
  status:              UserStatus;
  isSystemUser:        boolean;
  lastLoginDate?:      string | null;
  failedLoginAttempts: number;
  createdDate:         string;
}

export interface UserRoleSummaryDto {
  roleId:      string;
  roleCode:    string;
  roleName:    string;
  grantedDate: string;
}

export interface UserLocationAccessDto {
  locationId:   string;
  locationCode: string;
  locationName: string;
  isDefault:    boolean;
}

export interface UserCostCenterAccessDto {
  costCenterId:   string;
  costCenterCode: string;
  costCenterName: string;
  isDefault:      boolean;
}

export interface UserCompanyAccessDto {
  companyId:   string;
  companyCode: string;
  companyName: string;
  isDefault:   boolean;
}

// ── Filters ───────────────────────────────────────────────────────────────────

export interface UserFilterDto {
  searchTerm?:     string;
  status?:         string;
  roleId?:         string;
  pageNumber?:     number;
  pageSize?:       number;
  sortBy?:         string;
  sortDescending?: boolean;
}

// ── Request payloads ─────────────────────────────────────────────────────────

export interface CreateUserRequest {
  username:     string;
  email:        string;
  password:     string;
  firstName:    string;
  lastName:     string;
  phoneNumber?: string | null;
  userCode?:    string | null;
  displayName?: string | null;
  employeeRef?: string | null;
  userType?:    string | null;
  remarks?:     string | null;
  roleIds?:     string[];
}

export interface UpdateUserRequest {
  firstName?:           string | null;
  lastName?:            string | null;
  email?:               string | null;
  phoneNumber?:         string | null;
  userCode?:            string | null;
  displayName?:         string | null;
  employeeRef?:         string | null;
  userType?:            string | null;
  remarks?:             string | null;
  defaultLocationId?:   string | null;
  defaultCostCenterId?: string | null;
}

export interface ResetPasswordRequest {
  newPassword:        string;
  forceChangeOnLogin: boolean;
}

export interface AssignRolesRequest {
  roleIds: string[];
}

export interface AssignLocationAccessRequest {
  ids: string[];
}

export interface AssignCostCenterAccessRequest {
  ids: string[];
}

export interface AssignCompanyAccessRequest {
  ids:       string[];
  defaultId: string | null;
}

// ── Display helpers ────────────────────────────────────────────────────────────

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  Active:            'Active',
  Inactive:          'Inactive',
  Locked:            'Locked',
  PendingActivation: 'Pending Activation',
};

export const USER_STATUS_COLORS: Record<UserStatus, string> = {
  Active:            'bg-green-100 text-green-700',
  Inactive:          'bg-gray-100 text-gray-600',
  Locked:            'bg-red-100 text-red-700',
  PendingActivation: 'bg-amber-100 text-amber-700',
};

export const USER_TYPE_LABELS: Record<string, string> = {
  Standard: 'Standard User',
  External: 'External User',
  Service:  'Service Account',
};

/** Avatar colour based on name initial (consistent per-user) */
export const avatarColor = (name: string): string => {
  const colours = [
    'bg-indigo-500', 'bg-violet-500', 'bg-blue-500', 'bg-teal-500',
    'bg-emerald-500', 'bg-rose-500', 'bg-orange-500', 'bg-cyan-500',
  ];
  const idx = (name.charCodeAt(0) || 0) % colours.length;
  return colours[idx];
};

/** What a user's access can be granted over, nested as the access tree shows it. */
export interface AssignableScopeDto {
  companies: AssignableCompanyDto[];
  /** Cost centres belonging to no location; they hang off nothing and would otherwise vanish. */
  unassignedCostCentres: AssignableCostCentreDto[];
}

export interface AssignableCompanyDto {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  locations: AssignableLocationDto[];
}

export interface AssignableLocationDto {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  costCentres: AssignableCostCentreDto[];
}

export interface AssignableCostCentreDto {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}
